import { useCallback, useEffect, useState } from 'react';
import { EyeOffIcon, ShieldAlertIcon, XIcon } from 'lucide-react';
import {
  account,
  bridge,
  notes as notesApi,
  track,
  BridgeError,
  type NoteItem,
  type NotesSnapshot,
} from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { SelectRow } from '@/components/SelectRow';
import { TaskProgressCard } from '@/components/task-ui';
import { PageSkeleton, SkeletonRow } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CARD, MUTED } from '@/components/ui';

/** 原生回传 ISO 时间；不改时区，只取日期当副标题。 */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** 11729 → 「11,729」；拿不到值返回 null。 */
function formatCount(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value).toLocaleString('zh-CN');
}

/** 0.549 → 「54.9%」 */
function formatRate(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `${(Number(value) * 100).toFixed(1)}%`;
}

/**
 * 播放 / 点赞 / 评论 一行。
 *
 * 刻意只放三项：内容区实测只有 ~200px（右侧日期 + 勾选框占掉不少），
 * 再塞「收藏」就会折行，行高翻倍、看着拥挤。
 */
function statsLine(note: NoteItem): string {
  const stats = note.statistics;
  if (!stats) return '';
  const parts: string[] = [];
  const add = (label: string, value: number | null | undefined) => {
    const text = formatCount(value);
    if (text) parts.push(`${label} ${text}`);
  };
  add('播放', stats.play);
  add('点赞', stats.digg);
  add('评论', stats.comment);
  return parts.join(' · ');
}

/**
 * 完播 / 跳出 一行 —— 判断「这条值不值得藏起来」的主要依据。
 * 接口给的比率是 0~1 小数，这里转成百分比；同样只放两项以防折行。
 */
function depthLine(note: NoteItem): string {
  const metrics = note.metrics;
  if (!metrics) return '';
  const parts: string[] = [];
  const completion = formatRate(metrics.completion_rate_5s ?? metrics.completion_rate);
  if (completion) parts.push(`5s完播 ${completion}`);
  const bounce = formatRate(metrics.bounce_rate_2s);
  if (bounce) parts.push(`2s跳出 ${bounce}`);
  return parts.join(' · ');
}

/**
 * 中间滚动区：整页只有这一块能滚（顶部账号/状态与底部操作条分别固定）。
 * 高度由 PageShell 的 `fill` 撑成「剩余空间」，所以是动态的，不会预先设死。
 */
const SCROLL_REGION =
  'min-h-0 flex-1 overflow-y-auto overscroll-contain -mr-1 pr-1 ' +
  '[scrollbar-width:thin] [scrollbar-color:var(--muted-foreground)_transparent] ' +
  '[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent ' +
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/50';

/**
 * 百宝箱「笔记处理」：批量把抖音笔记设为「仅自己可见」。
 *
 * 结构照搬搬小书 `BackupPage`：选账号 → 勾选 → 跑，进度靠 `notes.progress`
 * 事件推送（不轮询）。笔记列表读的是**本机备份**的笔记，实际改的是抖音上的可见性。
 */
export function NotesPage() {
  const { loading: infoLoading, isNative } = useAppInfo();
  const {
    accounts,
    selectedId,
    setSelectedId,
    error: accountError,
    loading: accountsLoading,
  } = useAccounts();

  const [items, setItems] = useState<NoteItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [snapshot, setSnapshot] = useState<NotesSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 列表分类：全部 / 未隐藏 / 已隐藏 */
  const [tab, setTab] = useState<'all' | 'visible' | 'hidden'>('all');
  const [syncing, setSyncing] = useState(false);

  const loaded = !infoLoading && !accountsLoading;
  useAppReady(loaded);

  // 埋点：页面访问
  useEffect(() => {
    track.pageOpen('notes');
  }, []);

  // 进度用事件推送，不轮询（PROTOCOL.md §5）
  useEffect(
    () =>
      bridge.on('notes.progress', (payload) => {
        const next = payload as NotesSnapshot;
        setSnapshot(next);
        // 刚成功的就地翻成「已隐藏」—— 实时处理实时反馈，
        // 不用等用户再点一次「同步状态」抓接口。
        const doneIds = next.doneIds;
        if (doneIds && doneIds.length > 0) {
          setItems((prev) =>
            prev.map((note) =>
              doneIds.includes(note.awemeId) && !note.isPrivate
                ? { ...note, isPrivate: true }
                : note,
            ),
          );
        }
        // 全部成功就清掉选中 —— 这批已经处理完，不该继续勾着、
        // 按钮上也不该还挂着（N）。有失败或被取消时保留选中，
        // 方便直接重试没过的那几条。
        if (next.state === 'completed' && (next.failed ?? 0) === 0) {
          setSelectedIds([]);
        }
      }),
    [],
  );

  const currentAccount = accounts.find((a) => a.id === selectedId) ?? null;
  const canWrite = currentAccount?.canWrite ?? false;
  const running = snapshot?.state === 'running';
  const writableCount = accounts.filter((a) => a.canWrite).length;

  // 切账号时重新拉该账号的笔记（同时丢掉上一个账号的批次结果）
  useEffect(() => {
    setSnapshot(null);
    if (!currentAccount) {
      setItems([]);
      setTotal(0);
      setSelectedIds([]);
      return;
    }
    let cancelled = false;
    setListLoading(true);
    setError(null);
    notesApi
      .list(currentAccount.id)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setSelectedIds([]);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e));
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAccount?.id]);

  const switchAccount = useCallback(async () => {
    try {
      setSelectedId((await account.pick('authenticated')).id);
    } catch {
      // 用户取消：保留当前账号
    }
  }, [setSelectedId]);

  /** 用户重新挑笔记时撤掉上一批的完成态，不打断进行中的批次。 */
  const dismissFinished = useCallback(() => {
    setSnapshot((prev) =>
      prev && (prev.state === 'completed' || prev.state === 'cancelled') ? null : prev,
    );
  }, []);

  const toggle = useCallback(
    (awemeId: string) => {
      dismissFinished();
      setSelectedIds((prev) =>
        prev.includes(awemeId) ? prev.filter((id) => id !== awemeId) : [...prev, awemeId],
      );
    },
    [dismissFinished],
  );

  const hiddenCount = items.filter((n) => n.isPrivate === true).length;
  const visibleCount = items.length - hiddenCount;
  const tabs: { key: 'all' | 'visible' | 'hidden'; label: string; count: number }[] = [
    { key: 'all', label: '全部', count: items.length },
    { key: 'visible', label: '未隐藏', count: visibleCount },
    { key: 'hidden', label: '已隐藏', count: hiddenCount },
  ];
  const shownItems =
    tab === 'hidden'
      ? items.filter((n) => n.isPrivate === true)
      : tab === 'visible'
        ? items.filter((n) => n.isPrivate !== true)
        : items;

  const allSelected =
    shownItems.length > 0 && shownItems.every((n) => selectedIds.includes(n.awemeId));

  /** 向抖音同步一次「仅自己可见」状态，再重读列表拿最新缓存。 */
  /**
   * 向抖音同步一次「仅自己可见」状态，再重读列表拿最新缓存。
   *
   * `silent` 供自动补拉使用：原生版本还没提供 `notes.refreshVisibility` 时
   * （站点先发、App 后发），静默跳过，不要在页面上挂一条红色错误。
   */
  const refreshVisibility = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!currentAccount || syncing) return;
      setSyncing(true);
      try {
        await notesApi.refreshVisibility(currentAccount.id);
        const res = await notesApi.list(currentAccount.id);
        setItems(res.items);
        setTotal(res.total);
        setError(null);
      } catch (e) {
        if (silent && e instanceof BridgeError && e.code === 'unknown_method') {
          // 老版本原生：继续按「全部」展示即可
        } else {
          setError(describeError(e));
        }
      } finally {
        setSyncing(false);
      }
    },
    [currentAccount?.id, syncing],
  );

  // 首次进来如果本地还没有可见性快照，后台同步一次（列表本身不受影响）
  useEffect(() => {
    if (!currentAccount?.canWrite || items.length === 0) return;
    if (items.some((n) => n.isPrivate !== undefined)) return;
    void refreshVisibility({ silent: true });
    // 只在「一条都不知道可见性」时补拉一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.id, items.length]);

  // 换分类时清掉选中，避免跨分类把不相干的笔记一起提交
  useEffect(() => {
    setSelectedIds([]);
  }, [tab]);

  const run = useCallback(async () => {
    if (!currentAccount || selectedIds.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      setSnapshot(await notesApi.setPrivate(currentAccount.id, selectedIds));
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }, [currentAccount?.id, selectedIds, busy]);

  const cancel = useCallback(async () => {
    try {
      setSnapshot(await notesApi.cancel());
    } catch (e) {
      setError(describeError(e));
    }
  }, []);

  const done = snapshot?.done ?? 0;
  const failed = snapshot?.failed ?? 0;
  const planned = snapshot?.total ?? 0;

  const errorMessage = error ?? accountError;

  return (
    <PageShell
      title="笔记处理"
      description="批量把抖音笔记设为「仅自己可见」，不删除"
      backTo="/"
      fill
    >
      {!loaded ? (
        <PageSkeleton kind="notes" rows={4} />
      ) : (
        <>
          {/* 顶部固定：账号、提示与当前任务状态 */}
          <div className="flex shrink-0 flex-col gap-4">
            {currentAccount ? (
              <AccountCard
                account={currentAccount}
                label="处理账号"
                detail={writableCount > 1 ? `${writableCount} 个账号可切换` : undefined}
                onClick={writableCount > 1 ? () => void switchAccount() : undefined}
              />
            ) : null}

            {!isNative ? (
              <Card className={CARD}>
                <p className="text-muted-foreground text-xs">
                  当前不在 App 内。浏览器里单独调试请加 <code>?mock=1</code>。
                </p>
              </Card>
            ) : null}

            {!currentAccount ? (
              <Card className={CARD}>
                <div className="flex items-center gap-2">
                  <ShieldAlertIcon className="text-primary size-5 shrink-0" />
                  <strong className="text-sm font-semibold">没有可处理的账号</strong>
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  需要至少一个账号，请先在 App 内登录后再来。
                </p>
              </Card>
            ) : null}

            {running ? (
              <TaskProgressCard
                title="正在设为仅自己可见"
                right={planned > 0 ? `已完成 ${done} / ${planned} 条` : undefined}
                ratio={planned > 0 ? done / planned : undefined}
                action={
                  <p className="text-muted-foreground text-xs">
                    {snapshot?.current ? `正在处理：${snapshot.current}` : '逐条提交，每条间隔 1 秒'}
                  </p>
                }
              />
            ) : null}

            {snapshot?.state === 'completed' ? (
              <TaskProgressCard
                finished
                bar={false}
                title="已设为仅自己可见"
                detail={`${done} 条成功${failed > 0 ? `，${failed} 条失败` : ''}`}
                note={
                  snapshot.error
                    ? `最近一次错误：${describeError(snapshot.error)}`
                    : undefined
                }
              />
            ) : null}

            {snapshot?.state === 'cancelled' ? (
              <TaskProgressCard
                bar={false}
                icon={<XIcon className="text-destructive size-5 shrink-0" />}
                title="已取消"
                detail={`完成 ${done} 条，剩余未处理`}
              />
            ) : null}
          </div>

          {/* 中间：整页唯一的滚动区，高度 = 视口减去上下两块 */}
          <div className={SCROLL_REGION}>
            {currentAccount ? (
              <Card className={CARD}>
                {/* 分类 tab：全部 / 未隐藏 / 已隐藏 */}
                <div className="flex items-center gap-1">
                  {tabs.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setTab(item.key)}
                      className={
                        tab === item.key
                          ? 'bg-foreground/10 text-foreground rounded-full px-2.5 py-0.5 text-xs'
                          : 'text-muted-foreground rounded-full px-2.5 py-0.5 text-xs'
                      }
                    >
                      {item.label} {item.count}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="text-primary ml-auto text-xs font-medium disabled:opacity-50"
                    disabled={syncing}
                    onClick={() => void refreshVisibility()}
                  >
                    {syncing ? '同步中…' : '同步状态'}
                  </button>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <p className={MUTED}>
                    {listLoading ? '正在读取笔记…' : `本机已备份 ${total} 条笔记`}
                  </p>
                  {shownItems.length > 0 && !listLoading ? (
                    <button
                      type="button"
                      className="text-primary text-xs font-medium"
                      onClick={() => {
                        dismissFinished();
                        setSelectedIds(
                          allSelected ? [] : shownItems.map((n) => n.awemeId),
                        );
                      }}
                    >
                      {allSelected ? '清空' : '全选'}
                    </button>
                  ) : null}
                </div>

                {listLoading ? (
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: 4 }, (_, index) => (
                      <SkeletonRow key={index} />
                    ))}
                  </div>
                ) : null}

                {!listLoading && items.length === 0 ? (
                  <p className="text-muted-foreground text-xs leading-snug">
                    这个账号还没有备份过笔记。先到「数据备份」勾选作品，备份完成后回来处理。
                  </p>
                ) : null}

                {!listLoading && items.length > 0 && shownItems.length === 0 ? (
                  <p className="text-muted-foreground text-xs leading-snug">
                    {tab === 'hidden'
                      ? '还没有已隐藏的笔记。想把某条藏起来就勾选后点「设为仅自己可见」。'
                      : '这些笔记目前都是公开可见的。'}
                  </p>
                ) : null}

                {!listLoading && shownItems.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {shownItems.map((note) => (
                      <SelectRow
                        key={note.awemeId}
                        checked={selectedIds.includes(note.awemeId)}
                        onCheckedChange={() => toggle(note.awemeId)}
                        disabled={running}
                        detail={formatDate(note.createdAt)}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          {note.cover ? (
                            <img
                              src={note.cover}
                              alt=""
                              loading="lazy"
                              className="size-9 shrink-0 rounded object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : null}
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate">{note.desc || '无作品描述'}</span>
                          {note.isPrivate ? (
                            <span className="bg-muted text-muted-foreground shrink-0 rounded px-1 py-px text-[10px]">
                              已隐藏
                            </span>
                          ) : null}
                        </span>
                        {statsLine(note) ? (
                          <span className="text-caption1 text-muted-foreground truncate block tabular-nums">
                            {statsLine(note)}
                          </span>
                        ) : null}
                        {depthLine(note) ? (
                          <span className="text-caption1 text-primary truncate block tabular-nums">
                            {depthLine(note)}
                          </span>
                        ) : null}
                      </span>
                        </span>
                      </SelectRow>
                    ))}
                  </div>
                ) : null}
              </Card>
            ) : null}
          </div>

          {/* 底部固定：操作条与错误提示，永远停在视口底部 */}
          <div className="shrink-0">
            {currentAccount ? (
              <Card className={CARD}>
                <Button
                  className="w-full"
                  loading={busy}
                  disabled={
                    running || listLoading || selectedIds.length === 0 || !canWrite
                  }
                  onClick={() => void run()}
                >
                  {!busy ? <EyeOffIcon data-icon="inline-start" /> : null}
                  {busy ? '提交中…' : `设为仅自己可见（${selectedIds.length}）`}
                </Button>
                {!canWrite ? (
                  <p className="text-muted-foreground text-xs leading-snug">
                    该账号未登录或登录已过期，请先在 App 内重新登录后再处理。
                  </p>
                ) : null}
                {running ? (
                  <Button variant="secondary" className="w-full" onClick={() => void cancel()}>
                    <XIcon data-icon="inline-start" />
                    取消
                  </Button>
                ) : null}
                {selectedIds.length > 0 ? (
                  <p className="text-muted-foreground text-xs">
                    仅自己可见 = 抖音上的「谁可以看 → 仅自己可见」，笔记不会被删除。
                  </p>
                ) : null}
              </Card>
            ) : null}

            {errorMessage ? (
              <p className="text-destructive mt-2 px-1 text-sm">{errorMessage}</p>
            ) : null}
          </div>
        </>
      )}
    </PageShell>
  );
}
